import {Injectable, Injector} from '@angular/core'
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http'
import {Router} from '@angular/router'
import {throwError as observableThrowError, Observable} from 'rxjs'
import {catchError, switchMap} from 'rxjs/operators'
import {AuthenticationService} from '@app/core/services/authentication.service'
import {NotificationService} from '@app/core/services/notification.service'
import {AuthHelperService} from '@app/core/services/auth-helper.service'
import {LoggerService} from '@app/core/services/logger.service'
import {LocalStorageService} from './local-storage.service'
import {LogoutCoordinatorService} from './logout-coordinator.service'

@Injectable()
export class AuthFailureInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private notify: NotificationService,
    private helper: AuthHelperService,
    private logger: LoggerService,
    private store: LocalStorageService,
    private logoutCoordinator: LogoutCoordinatorService,
    private injector: Injector,
  ) {}

  shownOnlyOnce = false

  signOut(): boolean {
    if (!this.logoutCoordinator.begin('http-401')) {
      return false
    }

    // let the auth redirect steer us
    if (!this.shownOnlyOnce) {
      this.notify.error('Your session has expired. Please log in again.')
      this.shownOnlyOnce = true
      // reset after 30s
      setTimeout(() => {
        this.shownOnlyOnce = false
      }, 30000)
    }
    // Make sure no token is left behind when unauthorized. Clear the WHOLE
    // session, not just the CURO keys: a bearer (Keycloak) session also stores
    // a refresh token and id token. Leaving the refresh token behind lets the
    // still-armed silent-refresh timer (in AuthenticationService) mint a fresh
    // access token and silently resurrect the very session the backend just
    // rejected. With the refresh token gone that timer fails cleanly and
    // cancels itself on its next tick.
    this.helper.token = null
    this.helper.tokenType = 'curo'
    this.store.removeItem('auth-token')
    this.store.removeItem('auth-token-type')
    this.store.removeItem('refresh-token')
    this.store.removeItem('sso-id-token')
    this.logoutCoordinator.record('local-session-cleared', 'http-401')
    this.logoutCoordinator.suppressNextAutomaticLogin('http-401')
    this.logoutCoordinator.record('local-login-navigation-started', 'http-401')
    this.helper.redirect()
    return true
  }

  private refreshAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler,
    original: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    // Resolved lazily: AuthenticationService depends on HttpClient, which
    // depends on this interceptor, so a constructor injection would cycle.
    const auth = this.injector.get(AuthenticationService)
    return auth.refreshBearerToken().pipe(
      switchMap((ok) => {
        if (!ok || !this.helper.token) {
          // refreshBearerToken already cleared the session and redirected.
          this.signOut()
          return observableThrowError(() => original)
        }
        const retried = req.clone({
          headers: req.headers.set(
            'Authorization',
            'Bearer ' + this.helper.token,
          ),
        })
        /* The retried request only travels the chain DOWNSTREAM of this
           interceptor, so a second 401 never re-enters intercept(). End the
           session here, or a token the backend refuses even though it is
           fresh (deactivated user, broken mapping) leaves a "live" session
           where every call fails. Also the natural cap: refresh + replay
           happen at most once per original request. */
        return next.handle(retried).pipe(
          catchError((retryErr: HttpErrorResponse) => {
            if (retryErr.status === 401) {
              this.signOut()
            }
            return observableThrowError(() => retryErr)
          }),
        )
      }),
    )
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        this.logger.log(err)
        if (err.status === 0) {
          // cannot connect to service
          // show onscreen notification
          this.notify.error('MyPayter service is unavailable at this time.')
          // let the auth redirect steer us
          // this.helper.redirect();
        }

        if (err.status === 400) {
          if (err.error.description) {
            if (err.error.description.includes('Elasticsearch exception')) {
              this.notify.error(
                'Something went wrong, please try to adjust your search.',
              )
            } else if (
              err.error.description.includes('failed to authenticate')
            ) {
              // do nothing
            } else if (
              err.error.description.includes('Domain is already inactive')
            ) {
              // do nothing
            } else {
              this.notify.error(err.error.description)
            }
          } else if (err.error.errorMessage) {
          }
          // show onscreen notification
          else {
            this.notify.error('Something went wrong, please try again later.')
          }
        }

        if (err.status === 401) {
          // not authenticated
          // show onscreen notification

          /* Except the SSO who-am-I: its 401 happens while the user is still
             mid-login, and `bootstrapBearerSession` owns that error (it
             stores a login-page message). Redirecting from here too would
             race the AuthGuard's redirect, re-create the login component,
             and eat that message. */
          if (!req.url.includes('Whoami')) {
            /* A Keycloak bearer token may simply have expired between the
               silent-refresh timer ticks (throttled background tab, clock
               skew, laptop sleep). Refresh once and replay the request before
               treating the 401 as the end of the session. */
            if (this.helper.tokenType === 'bearer') {
              return this.refreshAndRetry(req, next, err)
            }
            const logoutStarted = this.signOut()
            if (
              logoutStarted &&
              err.error?.code !== 1002 &&
              err.error?.code !== 1001
            ) {
              this.notify.error(err.message)
            }
          }
        }
        if (err.status === 403) {
          // access denied
          // show onscreen notification
          this.notify.error(err.message)
        }
        // pass better information to users: https://payter.atlassian.net/browse/CURO-2092
        if (
          err.hasOwnProperty('error') &&
          err.error.hasOwnProperty('description') &&
          err.error.description ===
            'password does not meet minimum requirements'
        ) {
          err.error.description =
            'Password needs to contain at least 8 characters and must contain an upper case character, a lower case character, a number, and a special character(!@#$%^&*(){}[]<>?|/)'
        }
        return observableThrowError(err)
      }),
    )
  }
}
